import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const STORE_API_URL = process.env.REACT_APP_API_URL;

function OrphanedImagesManager() {
  const { token } = useContext(AuthContext);
  const [selectedStore, setSelectedStore] = useState('');
  const [orphanedImagesData, setOrphanedImagesData] = useState(null);
  const [selectedOrphanedImages, setSelectedOrphanedImages] = useState([]);
  const [deletingOrphanedImages, setDeletingOrphanedImages] = useState(false);
  const [loadingOrphanedImages, setLoadingOrphanedImages] = useState(false);
  const [stores, setStores] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [failedImages, setFailedImages] = useState(new Set());

  useEffect(() => {
    if (token) {
      fetchStores();
    }
  }, [token]);

  // Fetch stores for the dropdown
  const fetchStores = async () => {
    try {
      const response = await fetch(`${STORE_API_URL}/super/stores`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stores.');
      }

      const data = await response.json();
      setStores(data);
    } catch (error) {
      toast.error(`Error fetching stores: ${error.message}`);
    }
  };

  // Function to handle image load errors
  const handleImageError = (imageKey) => {
    setFailedImages(prev => new Set([...prev, imageKey]));
  };

  // Function to open image preview modal
  const openImagePreview = (imageKey, imageUrl) => {
    // Don't attempt to preview failed images
    if (failedImages.has(imageKey)) {
      toast.warning("Preview not available for this image");
      return;
    }
    
    setPreviewImage({
      url: imageUrl,
      key: imageKey
    });
  };
  
  // Function to close image preview modal
  const closeImagePreview = () => {
    setPreviewImage(null);
  };

  // Find orphaned images for a store
  const findOrphanedImages = async () => {
    if (!selectedStore) return;
    
    setLoadingOrphanedImages(true);
    try {
      const response = await fetch(`${STORE_API_URL}/super/orphaned-product-images/${selectedStore}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to find orphaned images');
      }

      const data = await response.json();
      setOrphanedImagesData(data);
      setSelectedOrphanedImages([]);
      
      // Reset failed images when loading new orphaned images
      setFailedImages(new Set());
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoadingOrphanedImages(false);
    }
  };

  // Delete selected orphaned images
  const deleteSelectedOrphanedImages = async () => {
    if (!selectedStore || selectedOrphanedImages.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedOrphanedImages.length} orphaned images? This cannot be undone.`)) {
      return;
    }
    
    setDeletingOrphanedImages(true);
    try {
      const response = await fetch(`${STORE_API_URL}/super/orphaned-product-images/${selectedStore}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ keys: selectedOrphanedImages }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete orphaned images');
      }

      const result = await response.json();
      toast.success(`Successfully deleted ${result.successful} images. ${result.failed} failed.`);
      
      // Refresh the list after deletion
      findOrphanedImages();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setDeletingOrphanedImages(false);
    }
  };

  // Format bytes to human-readable size
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  return (
    <div className="container">
      <ToastContainer />
      <div className="headline d-flex justify-content-between align-items-center mb-4">
        <h2 className="underline">Manage Orphaned Product Images</h2>
        <Link to="/superadmin" className="btn btn-outline-secondary">
          <i className="fas fa-arrow-left me-2"></i>Back to Admin
        </Link>
      </div>
      
      <div className="card">
        <div className="card-header">
          <h5>Find & Clean Up Orphaned Images</h5>
        </div>
        <div className="card-body">
          <p>
            Find and clean up product images that exist in storage but are no longer referenced by any product.
            These orphaned files consume storage space unnecessarily.
          </p>
          
          <div className="mb-3">
            <label className="form-label">Select Store:</label>
            <select 
              className="form-select" 
              value={selectedStore || ''}
              onChange={(e) => setSelectedStore(e.target.value)}
            >
              <option value="">-- Select Store --</option>
              {stores.map(store => (
                <option key={store._id} value={store._id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>
          
          <button 
            className="btn btn-primary" 
            onClick={findOrphanedImages}
            disabled={!selectedStore || loadingOrphanedImages}
          >
            {loadingOrphanedImages ? 'Scanning...' : 'Find Orphaned Images'}
          </button>
          
          {orphanedImagesData && (
            <div className="mt-4">
              <div className="alert alert-info">
                <h6>Scan Results for {orphanedImagesData.store_name}</h6>
                <p>
                  Found {orphanedImagesData.orphaned_count} orphaned images out of {orphanedImagesData.total_bucket_objects} total files.
                  ({orphanedImagesData.active_image_keys} images are still in use)
                </p>
              </div>
              
              {orphanedImagesData.orphaned_count > 0 && (
                <>
                  <div className="mb-3">
                    <div className="form-check">
                      <input 
                        className="form-check-input" 
                        type="checkbox" 
                        id="selectAllOrphaned"
                        checked={selectedOrphanedImages.length === orphanedImagesData.orphaned_objects.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedOrphanedImages(orphanedImagesData.orphaned_objects.map(obj => obj.key));
                          } else {
                            setSelectedOrphanedImages([]);
                          }
                        }}
                      />
                      <label className="form-check-label" htmlFor="selectAllOrphaned">
                        Select All ({formatBytes(orphanedImagesData.orphaned_objects.reduce((total, obj) => total + obj.size, 0))})
                      </label>
                    </div>
                  </div>
                  
                  <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table className="table table-sm table-striped">
                      <thead>
                        <tr>
                          <th width="5%"></th>
                          <th width="10%">Preview</th>
                          <th width="15%">Size</th>
                          <th width="15%">Last Modified</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orphanedImagesData.orphaned_objects.map((obj) => (
                          <tr key={obj.key}>
                            <td>
                              <input 
                                type="checkbox" 
                                className="form-check-input"
                                checked={selectedOrphanedImages.includes(obj.key)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedOrphanedImages([...selectedOrphanedImages, obj.key]);
                                  } else {
                                    setSelectedOrphanedImages(selectedOrphanedImages.filter(key => key !== obj.key));
                                  }
                                }}
                              />
                            </td>
                            <td>
                              {failedImages.has(obj.key) ? (
                                <div 
                                  className="placeholder-image" 
                                  style={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    backgroundColor: '#f0f0f0', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    fontSize: '10px',
                                    color: '#999',
                                    borderRadius: '4px'
                                  }}
                                  title="Image not available"
                                >
                                  N/A
                                </div>
                              ) : (
                                <img 
                                  src={obj.url} 
                                  alt="Preview" 
                                  className="img-thumbnail cursor-pointer" 
                                  style={{ width: '40px', height: '40px', objectFit: 'cover', cursor: 'pointer' }}
                                  onClick={() => openImagePreview(obj.key, obj.url)}
                                  onError={() => {
                                    handleImageError(obj.key);
                                  }}
                                />
                              )}
                            </td>
                            <td>{formatBytes(obj.size)}</td>
                            <td>{new Date(obj.last_modified).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-3">
                    <button
                      className="btn btn-danger"
                      disabled={selectedOrphanedImages.length === 0 || deletingOrphanedImages}
                      onClick={deleteSelectedOrphanedImages}
                    >
                      {deletingOrphanedImages ? 'Deleting...' : `Delete Selected (${selectedOrphanedImages.length})`}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <button type="button" className="btn-close" onClick={closeImagePreview}></button>
              </div>
              <div className="modal-body text-center">
                <img 
                  src={previewImage.url} 
                  alt="Preview" 
                  className="img-fluid" 
                  style={{ maxHeight: '70vh' }}
                  onError={(e) => {
                    handleImageError(previewImage.key);
                    e.target.src = 'https://via.placeholder.com/400?text=Image+Error';
                  }}
                />
              </div>
              <div className="modal-footer">
                <a href={previewImage.url} target="_blank" rel="noopener noreferrer" className="btn btn-primary">Open Full Size</a>
                <button type="button" className="btn btn-secondary" onClick={closeImagePreview}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrphanedImagesManager; 